import { FC, useEffect, useRef, useState } from "react";
import {
  DateFieldValue,
  DatePropsV2,
  FieldLabeledProps,
} from "../../types/fieldTypes";
import { FormGroup } from "@blueprintjs/core";
import { FieldLabel } from "../../CoreFields/FieldLabel/FieldLabel";
import {
  RenderDayCellEventArgs,
  ChangedEventArgs,
  DatePickerComponent,
} from "@syncfusion/ej2-react-calendars";
import { FormFieldErrorContainer } from "../../CoreFields/FieldError/FormFieldErrorContainer";
import { format } from "date-fns";
import moment from "moment";

export interface DateFieldV2Props extends DatePropsV2 {
  minDate?: Date;
  maxDate?: Date;
  showTodayButton?: boolean;
  showClearButton?: boolean;
  disableWeekends?: boolean;
  disabledDates?: Date[];
  /** Will be called before each date displayed on calendar: return true to enable, false to disable */
  dateFilter?: (date: Date) => boolean;
  dateFormat?: string;
  labelProps?: FieldLabeledProps;
}

export const DateFieldV2: FC<DateFieldV2Props> = (props) => {
  const calendarRef = useRef<DatePickerComponent | null>(null);
  const [localDate, setLocalDate] = useState<DateFieldValue>();

  useEffect(() => {
    setLocalDate(props.value);
  }, [props.value]);

  const parseValue = (value: DateFieldValue): Date => {
    return value ? moment(value).toDate() : (null as unknown as Date);
  };

  const formatValue = (value?: Date): DateFieldValue => {
    return value ? format(value, props.dateFormat ?? "yyyy-MM-dd") : "";
  };

  const handleRenderDayCell = (args: RenderDayCellEventArgs) => {
    const date = args.date;
    if (!date) {
      return;
    }
    if (
      // Disable Saturdays and Sundays if disableWeekends is true
      (props.disableWeekends && (date.getDay() === 0 || date.getDay() === 6)) ||
      // Disable dates included in disabledDates array
      props.disabledDates?.some(
        (d) =>
          d.getDate() === date.getDate() &&
          d.getMonth() === date.getMonth() &&
          d.getFullYear() === date.getFullYear()
      ) ||
      // Disable dates based on dateFilter
      (props.dateFilter && !props.dateFilter(date))
    ) {
      args.isDisabled = true;
    }
  };

  const handleChange = (args: ChangedEventArgs) => {
    const formattedValue = formatValue(args.value);
    setLocalDate(formattedValue);
    props.onSubmit && props.onSubmit(formattedValue);
  };

  // Reformat raw digit-only inputs like 12122025 → 12/12/2025 before Syncfusion parses them
  // (this strictly handles parsing into date that syncfusion will accept, not the formatting that is used for display)
  const handleBlur: React.FocusEventHandler<HTMLInputElement> = (e) => {
    const raw = e.currentTarget.value;
    // Fast-fail if contains slash or dash already
    if (/[-/]/.test(raw)) {
      return;
    }

    const digits = raw.replace(/\D/g, "");
    if (digits.length !== 8) {
      return; // not a pattern we handle
    }

    let month = "";
    let day = "";
    let year = "";

    //mmddyyyy
    if (digits.length === 8) {
      month = digits.slice(0, 2);
      day = digits.slice(2, 4);
      year = digits.slice(4, 8);
    }

    if (!month || !day || !year) {
      return;
    }

    const fixed = `${month.padStart(2, "0")}/${day.padStart(2, "0")}/${year}`;

    // Inject back into the input element so the user sees the formatted value
    e.currentTarget.value = fixed;

    // Let Syncfusion know about the new date by invoking existing change handler
    const parsed = moment(fixed, "MM/DD/YYYY", true).toDate();
    if (parsed.toString() !== "Invalid Date") {
      handleChange({ value: parsed } as unknown as ChangedEventArgs);
    }
  };

  return (
    <div className="ucl-base-input-date-field-v2-container">
      <FormGroup
        className={`${
          props.isRequired && !localDate
            ? "has-required-background"
            : "base-field"
        }`}
      >
        {props.label && (
          <FieldLabel
            labelName={props.label}
            tooltipDescription={props.description}
            fieldProps={{ ...props, value: localDate }}
            isRequired={props.isRequired}
            {...props.labelProps}
          />
        )}
        <div className="ucl-date-field-v2-picker-wrapper">
          <DatePickerComponent
            ref={calendarRef}
            min={props.minDate}
            max={props.maxDate}
            showTodayButton={props.showTodayButton}
            showClearButton={props.showClearButton}
            renderDayCell={handleRenderDayCell}
            value={parseValue(localDate)}
            change={handleChange}
            onBlur={handleBlur}
            enabled={!props.disabled}
            format={props.dateFormat}
          />
        </div>
        {props.errorMessages && (
          <FormFieldErrorContainer errorMessages={props.errorMessages} />
        )}
      </FormGroup>
    </div>
  );
};
